import React, { useContext } from 'react';
import { Space, Table } from 'antd';
import dayjs from 'dayjs';
import { GroupBuyingOrdersContext } from '../SettingAndManagement';
import CheckOrderDetail from '../CheckOrderDetail';
import AddNote from '../AddNote';
import AddOrEditOrder from '../AddOrEditModal';
import CancelGroupOrder from '../CancelGroupOrder';
import NoticeResidents from '../NoticeResidents';
import type { ColumnsType } from 'antd/es/table/interface';
import type { OrderForTableReceive } from '../types';
import type { GroupBuying } from '@/types/database/groupBuying';

interface OrdersListProps {
  listType: '現在團購' | '歷史資料';
}

const OrdersList = ({ listType }: OrdersListProps) => {
  const { groupBuyingOrders } = useContext(GroupBuyingOrdersContext);

  const sortTime = (
    a: OrderForTableReceive,
    b: OrderForTableReceive,
    timeType: string
  ) => {
    const aTime = dayjs(a[timeType]);
    const bTime = dayjs(b[timeType]);
    if (aTime.isBefore(bTime)) {
      return -1;
    } else if (aTime.isAfter(bTime)) {
      return 1;
    } else {
      return 0;
    }
  };

  const currentOrdersTableColumns: ColumnsType<OrderForTableReceive> = [
    {
      title: '團購名稱',
      dataIndex: 'itemName',
    },
    {
      title: '狀態',
      dataIndex: 'status',
    },
    {
      title: '團購上架日期(年/月/日)',
      dataIndex: 'establishTime',
      sorter: {
        compare: (a, b) => sortTime(a, b, 'establishTime'),
      },
    },
    {
      title: '團購起始日期(年/月/日)',
      dataIndex: 'startTime',
      sorter: {
        compare: (a, b) => sortTime(a, b, 'startTime'),
      },
    },
    {
      title: '團購截止日期(年/月/日)',
      dataIndex: 'endTime',
      sorter: {
        compare: (a, b) => sortTime(a, b, 'endTime'),
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record: OrderForTableReceive) => (
        <Space>
          <CheckOrderDetail order={record} />
          {record.status === '待上架' ? (
            <AddOrEditOrder orderRecord={record} />
          ) : (
            <AddNote order={record} />
          )}
          <CancelGroupOrder order={record} />
          <NoticeResidents order={record} />
        </Space>
      ),
    },
  ];

  const historyOrdersTableColumns: ColumnsType<OrderForTableReceive> = [
    {
      title: '團購名稱',
      dataIndex: 'itemName',
    },
    {
      title: '狀態',
      dataIndex: 'status',
    },
    {
      title: '團購起始日期(年/月/日)',
      dataIndex: 'startTime',
      sorter: {
        compare: (a, b) => sortTime(a, b, 'startTime'),
      },
    },
    {
      title: '團購截止日期(年/月/日)',
      dataIndex: 'endTime',
      sorter: {
        compare: (a, b) => sortTime(a, b, 'endTime'),
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record: OrderForTableReceive) => (
        <Space>
          <CheckOrderDetail order={record} />
          <AddNote order={record} />
          <CancelGroupOrder order={record} />
        </Space>
      ),
    },
  ];

  const getOrders = (
    groupBuyingOrders: GroupBuying[],
    listType: '現在團購' | '歷史資料'
  ) => {
    if (listType === '現在團購') {
      return groupBuyingOrders.filter((order) => {
        return order.status === '待上架' || order.status === '待成團';
      });
    } else {
      return groupBuyingOrders.filter((order) => {
        return order.status !== '待上架' && order.status !== '待成團';
      });
    }
  };

  const getTableDataSource = (
    groupBuyingOrders: GroupBuying[],
    listType: '現在團購' | '歷史資料'
  ) => {
    if (listType === '現在團購') {
      const currentOrders = getOrders(groupBuyingOrders, listType);
      return currentOrders.map((order) => {
        const { _id, itemName, status, establishTime, startTime, endTime } =
          order;
        return {
          ...order,
          key: _id,
          itemName,
          status,
          establishTime: dayjs.unix(establishTime).format('YYYY-MM-DD HH:mm'),
          startTime: dayjs.unix(startTime).format('YYYY-MM-DD HH:mm'),
          endTime: dayjs.unix(endTime).format('YYYY-MM-DD HH:mm'),
          action: [
            '查看',
            status === '待上架' ? '編輯' : '備註',
            '取消',
            '推播通知',
          ],
        };
      });
    } else {
      const historyOrders = getOrders(groupBuyingOrders, listType);
      return historyOrders.map((order) => {
        const { _id, itemName, status, establishTime, startTime, endTime } =
          order;
        return {
          ...order,
          key: _id,
          itemName: itemName,
          status: status,
          establishTime: dayjs.unix(establishTime).format('YYYY-MM-DD HH:mm'),
          startTime: dayjs.unix(startTime).format('YYYY-MM-DD HH:mm'),
          endTime: dayjs.unix(endTime).format('YYYY-MM-DD HH:mm'),
          action: ['查看', '備註', '取消'],
        };
      });
    }
  };

  return (
    <Table
      scroll={{ x: 'max-content' }}
      columns={
        listType === '現在團購'
          ? currentOrdersTableColumns
          : historyOrdersTableColumns
      }
      dataSource={getTableDataSource(groupBuyingOrders, listType)}
    />
  );
};

export default OrdersList;
